import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Settings } from 'lucide-react';

const Industrial = () => {
  return (
    <div>
      <SEO 
        title="Industrial Manufacturing Trade Compliance Services"
        description="Comprehensive trade compliance for industrial manufacturing, machinery, tools, and equipment. Expert guidance on tariffs, export controls, and customs regulations."
        keywords="industrial trade compliance, manufacturing export controls, machinery regulations, industrial equipment tariffs"
        path="/industries/industrial"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Industrial Manufacturing', path: '/industries/industrial' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Industrial Manufacturing Trade Compliance
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Industrial manufacturers face diverse trade compliance requirements spanning machinery exports, raw material imports, and global distribution. Our comprehensive services ensure compliance while optimizing duty costs.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Industrial Compliance Considerations</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-slate-50 rounded-lg p-8">
              <Settings className="h-10 w-10 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Machinery & Equipment</h3>
              <p className="text-gray-700">Industrial machinery may require EAR classification and licensing depending on capabilities and destination.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Raw Material Imports</h3>
              <p className="text-gray-700">Steel, aluminum, and specialty materials face various tariffs and trade restrictions requiring careful management.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">FTZ Opportunities</h3>
              <p className="text-gray-700">Industrial operations often benefit significantly from Foreign Trade Zone programs for manufacturing and assembly.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customs Valuation</h3>
              <p className="text-gray-700">Complex transfer pricing and related-party transactions require proper customs valuation compliance.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Industrial Manufacturing Services</h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              'Machinery and equipment export classification',
              'Raw material and component HTS classification',
              'Foreign Trade Zone implementation for manufacturers',
              'Customs valuation for related-party transactions',
              'Antidumping and countervailing duty compliance',
              'Trade agreement qualification (USMCA, FTAs)',
              'Temporary importation and re-export procedures',
              'Supply chain compliance program development'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-800">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Industrial Trade Compliance Consultation
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Optimize your industrial operations with comprehensive trade compliance guidance and duty savings strategies.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Industrial;